// src/services/file/FileService.ts
import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import type { TenantFile } from '@/types/File'
import type { IFirestoreService } from '@/platform/firebase/firestore'
import { storage } from '@/firebase/config'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { compressImage } from '@/utils/imageHelper'
import type { DocumentReference } from 'firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class FileService extends BaseTenantCrudService<TenantFile> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('files', firestore, session)
  }

  /**
   * Builds a storage path with tenant prefix
   * @param relativePath The relative path (e.g., 'attachments/doc-123/file.pdf')
   * @returns Full path with tenant prefix (e.g., 'tenants/tenant-123/attachments/doc-123/file.pdf')
   */
  buildStoragePath(relativePath: string): string {
    // Remove leading slash if present
    const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath
    return `tenants/${this.session.tenantId}/${cleanPath}`
  }

  /**
   * Validates that a storage path belongs to the current tenant
   * @param storagePath The storage path to validate
   * @throws Error if path doesn't belong to current tenant
   */
  private validateTenantPath(storagePath: string): void {
    const expectedPrefix = `tenants/${this.session.tenantId}/`
    if (!storagePath.startsWith(expectedPrefix)) {
      throw new Error(
        `Storage path must start with tenant prefix: ${expectedPrefix}. Got: ${storagePath}`,
      )
    }
  }

  /**
   * Uploads a file to Firebase Storage and returns the storage path and download URL
   * Similar to attachmentService.upload
   * @param file The file to upload
   * @param path The relative path where the file should be stored (will be prefixed with tenant)
   * @param options Optional: compress images
   * @returns Object containing storagePath and url
   */
  async upload(
    file: globalThis.File,
    path: string,
    options?: { compress?: boolean },
  ): Promise<{ storagePath: string; url: string }> {
    console.log('Starting file upload:', {
      path,
      file: { name: file.name, type: file.type, size: file.size },
    })

    let fileToUpload = file

    // Compress if it's an image and compression is requested
    if (options?.compress && file.type.startsWith('image/')) {
      console.log('Compressing image before upload')
      fileToUpload = await compressImage(file)
      console.log('Image compressed:', {
        originalSize: file.size,
        compressedSize: fileToUpload.size,
      })
    }

    const storagePath = this.buildStoragePath(path)
    const storageRef = ref(storage, storagePath)
    console.log('Uploading to storage reference:', storageRef.fullPath)

    try {
      await uploadBytes(storageRef, fileToUpload)
      console.log('File uploaded successfully')

      const url = await getDownloadURL(storageRef)
      console.log('Download URL generated:', url)

      return {
        storagePath,
        url,
      }
    } catch (error) {
      console.error('Error during file upload:', error)
      throw error
    }
  }

  /**
   * Uploads a file and saves metadata to Firestore
   * Similar to attachmentStore.uploadAndSaveAttachment
   * @param file The file to upload
   * @param linkedTo Document reference to link the file to
   * @param path The relative path where the file should be stored
   * @param metadata Optional metadata to include in the File document
   * @param options Optional: compress images
   * @returns Complete File object with id
   */
  async uploadAndSave(
    file: globalThis.File,
    linkedTo: DocumentReference,
    path: string,
    metadata?: Partial<TenantFile>,
    options?: { compress?: boolean },
  ): Promise<TenantFile> {
    console.log('Uploading and saving file:', {
      file: { name: file.name, type: file.type, size: file.size },
      linkedTo: linkedTo.path,
      path,
      metadata,
    })

    // Upload the file to storage
    const { storagePath, url } = await this.upload(file, path, options)

    // Create file object, using metadata where provided
    const fileData: Partial<TenantFile> = {
      name: metadata?.name || file.name,
      contentType: metadata?.contentType || file.type,
      size: metadata?.size || file.size,
      linkedTo,
      storagePath,
      url,
      notes: metadata?.notes || '',
      createdAt: metadata?.createdAt || new Date(),
      ...metadata, // Spread any additional metadata fields
    }

    // Save to Firestore (beforeCreate will inject createdAt, updatedAt, yardId, userId)
    const id = await this.create(fileData)
    const created = { ...fileData, id } as TenantFile

    console.log('File uploaded and saved:', created)
    return created
  }

  /**
   * Deletes a file from Firebase Storage only
   * Similar to attachmentService.delete
   * @param storagePath The path of the file to delete (must include tenant prefix)
   */
  async deleteFromStorage(storagePath: string): Promise<void> {
    console.log('Deleting file from storage:', storagePath)
    this.validateTenantPath(storagePath)
    const storageRef = ref(storage, storagePath)
    try {
      await deleteObject(storageRef)
      console.log('File deleted successfully')
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Deletes a file from both Firebase Storage and Firestore
   * @param fileId The ID of the file document in Firestore
   */
  async deleteFile(fileId: string): Promise<void> {
    console.log('Deleting file:', fileId)
    try {
      // Get the file document to get the storage path
      const file = await this.get(fileId)
      if (!file) {
        throw new Error(`File with id ${fileId} not found`)
      }

      // Delete from storage
      await this.deleteFromStorage(file.storagePath)

      // Delete from Firestore
      await super.delete(fileId)

      console.log('File deleted from both storage and Firestore')
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Gets the download URL for a file
   * @param storagePath The storage path (must include tenant prefix)
   * @returns Download URL
   */
  async getDownloadURL(storagePath: string): Promise<string> {
    this.validateTenantPath(storagePath)
    const storageRef = ref(storage, storagePath)
    return await getDownloadURL(storageRef)
  }
}

